import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Wrench, Plus, Trash, Spinner, FloppyDisk, CalendarCheck, Flask, CaretDown, PencilSimple,
  MagnifyingGlass, CaretLeft, CaretRight,
} from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CATEGORY_COLORS = {
  'Filter': { bg: '#E7F5EC', fg: '#047857' },
  'Machine accessories': { bg: '#EEF2FF', fg: '#4338CA' },
  'Plumbing fittings': { bg: '#FEF3C7', fg: '#A16207' },
  'Reagents': { bg: '#FBE7E7', fg: '#B91C1C' },
  'Handtools': { bg: '#E0F2FE', fg: '#0369A1' },
};

export const InstrumentationConfig = () => {
  return (
    <div className="space-y-6">
      <ToolsSection />
      <SchedulesSection />
      <QualityParamsSection />
    </div>
  );
};

function CollapsibleSection({ icon: Icon, title, subtitle, testid, count, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 p-6 text-left group"
        data-testid={`${testid}-toggle`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon size={18} weight="duotone" className="text-[#0A2540]" />
            <h3 className="text-lg font-bold text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{title}</h3>
            {typeof count === 'number' && (
              <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-full bg-[#0A2540]/5 text-[#0A2540]">{count}</span>
            )}
          </div>
          <p className="text-sm text-[#78716C]">{subtitle}</p>
        </div>
        <CaretDown
          size={16}
          weight="bold"
          className={`shrink-0 text-[#78716C] group-hover:text-[#0A2540] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
}

function ToolsSection() {
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [adding, setAdding] = useState(false);
  const [editTool, setEditTool] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 8;

  const load = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([
        axios.get(`${API_URL}/api/instrumentation/tools`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/instrumentation/tool-categories`, { headers: authHeaders() }),
      ]);
      setTools(Array.isArray(t.data) ? t.data : []);
      setCategories(Array.isArray(c.data) ? c.data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load tools'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addTool = async () => {
    if (!name.trim()) { toast.error('Tool name is required'); return; }
    if (!category) { toast.error('Select a category'); return; }
    setAdding(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/instrumentation/tools`,
        { name: name.trim(), category },
        { headers: authHeaders() },
      );
      setTools((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setName('');
      toast.success('Tool added');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to add tool'));
    } finally {
      setAdding(false);
    }
  };

  const removeTool = async (tool) => {
    try {
      await axios.delete(`${API_URL}/api/instrumentation/tools/${tool.id}`, { headers: authHeaders() });
      setTools((prev) => prev.filter((t) => t.id !== tool.id));
      toast.success(`"${tool.name}" removed`);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to remove tool'));
    }
  };

  const openEdit = (tool) => {
    setEditTool(tool);
    setEditName(tool.name);
    setEditCategory(tool.category);
  };

  const saveEdit = async () => {
    if (!editName.trim()) { toast.error('Tool name is required'); return; }
    setSavingEdit(true);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/instrumentation/tools/${editTool.id}`,
        { name: editName.trim(), category: editCategory },
        { headers: authHeaders() },
      );
      setTools((prev) => prev.map((t) => (t.id === data.id ? data : t)).sort((a, b) => a.name.localeCompare(b.name)));
      setEditTool(null);
      toast.success('Tool updated');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update tool'));
    } finally {
      setSavingEdit(false);
    }
  };

  const ql = search.trim().toLowerCase();
  const filtered = ql
    ? tools.filter((t) => t.name.toLowerCase().includes(ql) || t.category.toLowerCase().includes(ql))
    : tools;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <CollapsibleSection
      icon={Wrench}
      title="Tools"
      subtitle="Catalogue of tools available to instrumentation teams."
      testid="instr-tools-section"
      count={tools.length}
    >
      {/* Add form */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Tool Name</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. TDS Meter"
            className="h-9 text-sm w-[260px]"
            data-testid="instr-tool-name-input"
          />
        </div>
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Category</span>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 text-sm w-[220px]" data-testid="instr-tool-category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c} value={c} data-testid={`instr-cat-${c.toLowerCase().replace(/\s+/g, '-')}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={addTool}
          disabled={adding}
          className="h-9 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
          data-testid="instr-tool-add-btn"
        >
          {adding ? <Spinner size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
          Add Tool
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search tools by name or category…"
          className="h-9 pl-8 text-sm w-full sm:w-[340px]"
          data-testid="instr-tool-search"
        />
      </div>

      {/* Tool list */}
      {loading ? (
        <div className="py-8 text-center"><Spinner size={18} className="animate-spin text-[#0A2540] inline" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-[#78716C] py-6 text-center border border-dashed border-[#EDE6D8] rounded-lg">
          {tools.length === 0 ? 'No tools yet. Add the first one above.' : 'No tools match your search.'}
        </p>
      ) : (
        <div className="border border-[#EDE6D8] rounded-lg overflow-hidden">
          <div className="divide-y divide-[#EDE6D8]" data-testid="instr-tools-list">
            {paged.map((t) => {
            const c = CATEGORY_COLORS[t.category] || { bg: '#F5F5F4', fg: '#57534E' };
            return (
              <div key={t.id} className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white hover:bg-[#FCFAF6]/60" data-testid={`instr-tool-row-${t.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-sm font-medium text-[#1C1917] truncate">{t.name}</span>
                  <span
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0"
                    style={{ backgroundColor: c.bg, color: c.fg }}
                  >
                    {t.category}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => openEdit(t)}
                    className="text-[#0A2540] hover:bg-[#0A2540]/5 rounded-md p-1.5 transition"
                    title="Edit tool"
                    data-testid={`instr-tool-edit-${t.id}`}
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeTool(t)}
                    className="text-[#B91C1C] hover:bg-[#FBE7E7] rounded-md p-1.5 transition"
                    title="Remove tool"
                    data-testid={`instr-tool-delete-${t.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-[#EDE6D8] bg-[#FCFAF6]/60" data-testid="instr-tools-pagination">
              <span className="text-xs text-[#78716C]">
                Showing <span className="font-semibold text-[#1C1917]">{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)}</span> of{' '}
                <span className="font-semibold text-[#1C1917]">{filtered.length}</span> tools
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  data-testid="instr-tools-prev"
                  className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold text-[#0A2540] hover:bg-[#FCFAF6] transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CaretLeft size={11} weight="bold" /> Prev
                </button>
                <span className="text-xs font-mono text-[#0A2540] px-1">{safePage}/{totalPages}</span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  data-testid="instr-tools-next"
                  className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold text-[#0A2540] hover:bg-[#FCFAF6] transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <CaretRight size={11} weight="bold" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!editTool} onOpenChange={(o) => { if (!o) setEditTool(null); }}>
        <DialogContent className="max-w-md" data-testid="instr-tool-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Tool</DialogTitle>
            <DialogDescription>Update the tool name or category.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Tool Name</span>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-9 text-sm"
                data-testid="instr-tool-edit-name"
              />
            </div>
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Category</span>
              <Select value={editCategory} onValueChange={setEditCategory}>
                <SelectTrigger className="h-9 text-sm" data-testid="instr-tool-edit-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTool(null)} className="border-[#EDE6D8] rounded-lg" data-testid="instr-tool-edit-cancel">
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              disabled={savingEdit}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
              data-testid="instr-tool-edit-save"
            >
              {savingEdit ? <Spinner size={14} className="animate-spin mr-2" /> : <FloppyDisk size={14} className="mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CollapsibleSection>
  );
}

function SchedulesSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/instrumentation/maintenance-schedules`, { headers: authHeaders() })
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
      .catch((e) => toast.error(formatApiError(e, 'Failed to load maintenance schedules')))
      .finally(() => setLoading(false));
  }, []);

  const setField = (id, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value, _dirty: true } : r)));
  };

  const saveRow = async (row) => {
    const payload = {
      clean_applicable: row.clean_applicable,
      replace_applicable: row.replace_applicable,
      maintenance_action: (row.maintenance_action || '').trim(),
    };
    if (row.clean_applicable) {
      const clean = parseInt(row.clean_interval_days, 10);
      if (!Number.isFinite(clean) || clean < 1) { toast.error('Clean interval must be at least 1 day (or tick N/A)'); return; }
      payload.clean_interval_days = clean;
    }
    if (row.replace_applicable) {
      const replace = parseInt(row.replace_interval_days, 10);
      if (!Number.isFinite(replace) || replace < 1) { toast.error('Replacement interval must be at least 1 day (or tick N/A)'); return; }
      payload.replace_interval_days = replace;
    }
    const optionalNum = (v, label, parser) => {
      if (v === '' || v === null || v === undefined) return { ok: true, value: null };
      const n = parser(v);
      if (!Number.isFinite(n) || n < 1) { toast.error(`${label} must be at least 1`); return { ok: false }; }
      return { ok: true, value: n };
    };
    const vol = optionalNum(row.volume_trigger_liters, 'Volume trigger', parseFloat);
    if (!vol.ok) return;
    payload.volume_trigger_liters = vol.value;
    const insp = optionalNum(row.inspection_frequency_days, 'Inspection frequency', (v) => parseInt(v, 10));
    if (!insp.ok) return;
    payload.inspection_frequency_days = insp.value;
    setSavingId(row.id);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/instrumentation/maintenance-schedules/${row.id}`,
        payload,
        { headers: authHeaders() },
      );
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...data } : r)));
      toast.success(`${row.component} schedule saved`);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save schedule'));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <CollapsibleSection
      icon={CalendarCheck}
      title="Maintenance Schedules"
      subtitle="Clean and replacement intervals (in days) for store treatment components."
      testid="instr-schedules-section"
      count={rows.length}
    >
      {loading ? (
        <div className="py-8 text-center"><Spinner size={18} className="animate-spin text-[#0A2540] inline" /></div>
      ) : (
        <div className="border border-[#EDE6D8] rounded-lg overflow-x-auto">
          <div className="min-w-[1120px]">
          <div className="grid grid-cols-[minmax(150px,1fr)_170px_170px_180px_110px_110px_80px] gap-2 items-center px-4 py-2 bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">
            <span>Component</span>
            <span>Clean (Days)</span>
            <span>Time Trigger (Days)</span>
            <span>Maintenance Action</span>
            <span>Volume Trigger (L)</span>
            <span>Inspection Freq. (Days)</span>
            <span />
          </div>
          <div className="divide-y divide-[#EDE6D8]">
            {rows.map((r) => (
              <div key={r.id} className="grid grid-cols-[minmax(150px,1fr)_170px_170px_180px_110px_110px_80px] gap-2 items-center px-4 py-2 bg-white hover:bg-[#FCFAF6]/50" data-testid={`instr-schedule-row-${r.component.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="text-sm font-medium text-[#1C1917]">{r.component}</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    disabled={!r.clean_applicable}
                    value={r.clean_applicable ? (r.clean_interval_days ?? '') : ''}
                    placeholder={r.clean_applicable ? '' : 'N/A'}
                    onChange={(e) => setField(r.id, 'clean_interval_days', e.target.value)}
                    className="h-8 text-sm font-mono w-[90px] disabled:bg-[#FCFAF6] disabled:text-[#A8A29E]"
                    data-testid={`instr-clean-input-${r.component.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <Checkbox
                      checked={!r.clean_applicable}
                      onCheckedChange={(v) => setField(r.id, 'clean_applicable', !v)}
                      data-testid={`instr-clean-na-${r.component.toLowerCase().replace(/\s+/g, '-')}`}
                    />
                    <span className="text-[10px] font-semibold text-[#78716C]">N/A</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    disabled={!r.replace_applicable}
                    value={r.replace_applicable ? (r.replace_interval_days ?? '') : ''}
                    placeholder={r.replace_applicable ? '' : 'N/A'}
                    onChange={(e) => setField(r.id, 'replace_interval_days', e.target.value)}
                    className="h-8 text-sm font-mono w-[90px] disabled:bg-[#FCFAF6] disabled:text-[#A8A29E]"
                    data-testid={`instr-replace-input-${r.component.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <Checkbox
                      checked={!r.replace_applicable}
                      onCheckedChange={(v) => setField(r.id, 'replace_applicable', !v)}
                      data-testid={`instr-replace-na-${r.component.toLowerCase().replace(/\s+/g, '-')}`}
                    />
                    <span className="text-[10px] font-semibold text-[#78716C]">N/A</span>
                  </label>
                </div>
                <Input
                  value={r.maintenance_action ?? ''}
                  placeholder="e.g. Backwash"
                  onChange={(e) => setField(r.id, 'maintenance_action', e.target.value)}
                  className="h-8 text-sm"
                  data-testid={`instr-action-input-${r.component.toLowerCase().replace(/\s+/g, '-')}`}
                />
                <Input
                  type="number"
                  min="1"
                  value={r.volume_trigger_liters ?? ''}
                  placeholder="—"
                  onChange={(e) => setField(r.id, 'volume_trigger_liters', e.target.value)}
                  className="h-8 text-sm font-mono"
                  data-testid={`instr-volume-input-${r.component.toLowerCase().replace(/\s+/g, '-')}`}
                />
                <Input
                  type="number"
                  min="1"
                  value={r.inspection_frequency_days ?? ''}
                  placeholder="—"
                  onChange={(e) => setField(r.id, 'inspection_frequency_days', e.target.value)}
                  className="h-8 text-sm font-mono"
                  data-testid={`instr-inspection-input-${r.component.toLowerCase().replace(/\s+/g, '-')}`}
                />
                <Button
                  size="sm"
                  variant={r._dirty ? 'default' : 'outline'}
                  onClick={() => saveRow(r)}
                  disabled={savingId === r.id || !r._dirty}
                  className={`h-8 rounded-lg text-xs ${r._dirty ? 'bg-[#0A2540] hover:bg-[#0A2540]/90 text-white' : 'border-[#EDE6D8] text-[#78716C]'}`}
                  data-testid={`instr-schedule-save-${r.component.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {savingId === r.id ? <Spinner size={12} className="animate-spin" /> : <FloppyDisk size={12} className="mr-1" />}
                  Save
                </Button>
              </div>
            ))}
          </div>
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
}

const REQ_TYPES = [
  { value: 'range', label: 'Range (min – max)' },
  { value: 'max', label: 'Maximum' },
  { value: 'nil', label: 'NIL' },
  { value: 'absent', label: 'Absent' },
];

const renderRequirement = (p) => {
  if (p.requirement_type === 'range') return `${p.min_value} – ${p.max_value}`;
  if (p.requirement_type === 'max') return `≤ ${p.max_value}`;
  if (p.requirement_type === 'nil') return 'NIL';
  if (p.requirement_type === 'absent') return 'Absent';
  return '—';
};

function QualityParamsSection() {
  const [params, setParams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    parameter: '', category: '', requirement_type: '', min_value: '', max_value: '', comment: '',
  });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editParam, setEditParam] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const QP_PAGE_SIZE = 8;

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/instrumentation/quality-parameters`, { headers: authHeaders() }),
      axios.get(`${API_URL}/api/instrumentation/quality-categories`, { headers: authHeaders() }),
    ])
      .then(([p, c]) => {
        setParams(Array.isArray(p.data) ? p.data : []);
        setCategories(Array.isArray(c.data) ? c.data : []);
      })
      .catch((e) => toast.error(formatApiError(e, 'Failed to load quality parameters')))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const addParam = async () => {
    if (!form.parameter.trim()) { toast.error('Parameter name is required'); return; }
    if (!form.category) { toast.error('Select a category'); return; }
    if (!form.requirement_type) { toast.error('Select a conformity requirement'); return; }
    const payload = {
      parameter: form.parameter.trim(),
      category: form.category,
      requirement_type: form.requirement_type,
      comment: form.comment.trim(),
    };
    if (form.requirement_type === 'range') {
      if (form.min_value === '' || form.max_value === '') { toast.error('Enter both min and max values'); return; }
      payload.min_value = parseFloat(form.min_value);
      payload.max_value = parseFloat(form.max_value);
    } else if (form.requirement_type === 'max') {
      if (form.max_value === '') { toast.error('Enter the maximum value'); return; }
      payload.max_value = parseFloat(form.max_value);
    }
    setAdding(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/instrumentation/quality-parameters`, payload, { headers: authHeaders() },
      );
      setParams((prev) => [...prev, data].sort((a, b) =>
        a.category.localeCompare(b.category) || a.parameter.localeCompare(b.parameter)));
      setForm({ parameter: '', category: form.category, requirement_type: '', min_value: '', max_value: '', comment: '' });
      toast.success('Quality parameter added');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to add parameter'));
    } finally {
      setAdding(false);
    }
  };

  const removeParam = async (p) => {
    try {
      await axios.delete(`${API_URL}/api/instrumentation/quality-parameters/${p.id}`, { headers: authHeaders() });
      setParams((prev) => prev.filter((x) => x.id !== p.id));
      toast.success(`"${p.parameter}" removed`);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to remove parameter'));
    }
  };

  const openEdit = (p) => {
    setEditParam(p);
    setEditForm({
      parameter: p.parameter,
      category: p.category,
      requirement_type: p.requirement_type,
      min_value: p.min_value ?? '',
      max_value: p.max_value ?? '',
      comment: p.comment || '',
    });
  };

  const setEdit = (k, v) => setEditForm((prev) => ({ ...prev, [k]: v }));

  const saveEdit = async () => {
    if (!editForm.parameter.trim()) { toast.error('Parameter name is required'); return; }
    const payload = {
      parameter: editForm.parameter.trim(),
      category: editForm.category,
      requirement_type: editForm.requirement_type,
      comment: editForm.comment.trim(),
    };
    if (editForm.requirement_type === 'range') {
      if (editForm.min_value === '' || editForm.max_value === '') { toast.error('Enter both min and max values'); return; }
      payload.min_value = parseFloat(editForm.min_value);
      payload.max_value = parseFloat(editForm.max_value);
    } else if (editForm.requirement_type === 'max') {
      if (editForm.max_value === '') { toast.error('Enter the maximum value'); return; }
      payload.max_value = parseFloat(editForm.max_value);
    }
    setSavingEdit(true);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/instrumentation/quality-parameters/${editParam.id}`, payload, { headers: authHeaders() },
      );
      setParams((prev) => prev.map((x) => (x.id === data.id ? data : x)).sort((a, b) =>
        a.category.localeCompare(b.category) || a.parameter.localeCompare(b.parameter)));
      setEditParam(null);
      toast.success('Parameter updated');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update parameter'));
    } finally {
      setSavingEdit(false);
    }
  };

  const qpQl = search.trim().toLowerCase();
  const qpFiltered = qpQl
    ? params.filter((p) =>
        p.parameter.toLowerCase().includes(qpQl)
        || p.category.toLowerCase().includes(qpQl)
        || (p.comment || '').toLowerCase().includes(qpQl))
    : params;
  const qpTotalPages = Math.max(1, Math.ceil(qpFiltered.length / QP_PAGE_SIZE));
  const qpSafePage = Math.min(page, qpTotalPages);
  const qpPaged = qpFiltered.slice((qpSafePage - 1) * QP_PAGE_SIZE, qpSafePage * QP_PAGE_SIZE);

  return (
    <CollapsibleSection
      icon={Flask}
      title="Quality Parameters"
      subtitle="Water-quality parameters and their conformity requirements."
      testid="instr-quality-section"
      count={params.length}
    >
      {/* Add form */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Parameter</span>
          <Input
            value={form.parameter}
            onChange={(e) => set('parameter', e.target.value)}
            placeholder="e.g. pH"
            className="h-9 text-sm w-[180px]"
            data-testid="qp-name-input"
          />
        </div>
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Category</span>
          <Select value={form.category} onValueChange={(v) => set('category', v)}>
            <SelectTrigger className="h-9 text-sm w-[180px]" data-testid="qp-category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c} value={c} data-testid={`qp-cat-${c.toLowerCase()}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Conformity Requirement</span>
          <Select value={form.requirement_type} onValueChange={(v) => set('requirement_type', v)}>
            <SelectTrigger className="h-9 text-sm w-[170px]" data-testid="qp-req-select">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {REQ_TYPES.map((r) => (
                <SelectItem key={r.value} value={r.value} data-testid={`qp-req-${r.value}`}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {form.requirement_type === 'range' && (
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Min</span>
            <Input
              type="number" step="any"
              value={form.min_value}
              onChange={(e) => set('min_value', e.target.value)}
              className="h-9 text-sm w-[90px] font-mono"
              data-testid="qp-min-input"
            />
          </div>
        )}
        {(form.requirement_type === 'range' || form.requirement_type === 'max') && (
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Max</span>
            <Input
              type="number" step="any"
              value={form.max_value}
              onChange={(e) => set('max_value', e.target.value)}
              className="h-9 text-sm w-[90px] font-mono"
              data-testid="qp-max-input"
            />
          </div>
        )}
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Comment</span>
          <Input
            value={form.comment}
            onChange={(e) => set('comment', e.target.value)}
            placeholder="Optional note"
            className="h-9 text-sm w-[220px]"
            data-testid="qp-comment-input"
          />
        </div>
        <Button
          onClick={addParam}
          disabled={adding}
          className="h-9 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
          data-testid="qp-add-btn"
        >
          {adding ? <Spinner size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
          Add Parameter
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search parameters by name, category or comment…"
          className="h-9 pl-8 text-sm w-full sm:w-[380px]"
          data-testid="qp-search"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="py-8 text-center"><Spinner size={18} className="animate-spin text-[#0A2540] inline" /></div>
      ) : qpFiltered.length === 0 ? (
        <p className="text-sm text-[#78716C] py-6 text-center border border-dashed border-[#EDE6D8] rounded-lg">
          {params.length === 0 ? 'No quality parameters yet. Add the first one above.' : 'No parameters match your search.'}
        </p>
      ) : (
        <div className="border border-[#EDE6D8] rounded-lg overflow-hidden" data-testid="qp-list">
          <div className="grid grid-cols-[1.2fr_150px_160px_1.5fr_80px] gap-2 items-center px-4 py-2 bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">
            <span>Parameter</span>
            <span>Category</span>
            <span>Requirement</span>
            <span>Comment</span>
            <span />
          </div>
          <div className="divide-y divide-[#EDE6D8]">
            {qpPaged.map((p) => (
              <div key={p.id} className="grid grid-cols-[1.2fr_150px_160px_1.5fr_80px] gap-2 items-center px-4 py-2.5 bg-white hover:bg-[#FCFAF6]/50" data-testid={`qp-row-${p.id}`}>
                <span className="text-sm font-medium text-[#1C1917] truncate">{p.parameter}</span>
                <span
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full w-fit"
                  style={p.category === 'Microbiological'
                    ? { backgroundColor: '#FBE7E7', color: '#B91C1C' }
                    : { backgroundColor: '#E0F2FE', color: '#0369A1' }}
                >
                  {p.category}
                </span>
                <span className="text-sm font-mono text-[#0A2540]">{renderRequirement(p)}</span>
                <span className="text-xs text-[#78716C] truncate">{p.comment || '—'}</span>
                <div className="flex items-center gap-1 justify-self-end">
                  <button
                    type="button"
                    onClick={() => openEdit(p)}
                    className="text-[#0A2540] hover:bg-[#0A2540]/5 rounded-md p-1.5 transition"
                    title="Edit parameter"
                    data-testid={`qp-edit-${p.id}`}
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeParam(p)}
                    className="text-[#B91C1C] hover:bg-[#FBE7E7] rounded-md p-1.5 transition"
                    title="Remove parameter"
                    data-testid={`qp-delete-${p.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {qpTotalPages > 1 && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-[#EDE6D8] bg-[#FCFAF6]/60" data-testid="qp-pagination">
              <span className="text-xs text-[#78716C]">
                Showing <span className="font-semibold text-[#1C1917]">{(qpSafePage - 1) * QP_PAGE_SIZE + 1}–{Math.min(qpSafePage * QP_PAGE_SIZE, qpFiltered.length)}</span> of{' '}
                <span className="font-semibold text-[#1C1917]">{qpFiltered.length}</span> parameters
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={qpSafePage <= 1}
                  data-testid="qp-prev"
                  className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold text-[#0A2540] hover:bg-[#FCFAF6] transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CaretLeft size={11} weight="bold" /> Prev
                </button>
                <span className="text-xs font-mono text-[#0A2540] px-1">{qpSafePage}/{qpTotalPages}</span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(qpTotalPages, p + 1))}
                  disabled={qpSafePage >= qpTotalPages}
                  data-testid="qp-next"
                  className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold text-[#0A2540] hover:bg-[#FCFAF6] transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <CaretRight size={11} weight="bold" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!editParam} onOpenChange={(o) => { if (!o) setEditParam(null); }}>
        <DialogContent className="max-w-lg" data-testid="qp-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Quality Parameter</DialogTitle>
            <DialogDescription>Update the parameter, category, conformity requirement or comment.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Parameter</span>
                  <Input
                    value={editForm.parameter}
                    onChange={(e) => setEdit('parameter', e.target.value)}
                    className="h-9 text-sm"
                    data-testid="qp-edit-name"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Category</span>
                  <Select value={editForm.category} onValueChange={(v) => setEdit('category', v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="qp-edit-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Requirement</span>
                  <Select value={editForm.requirement_type} onValueChange={(v) => setEdit('requirement_type', v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="qp-edit-req">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REQ_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {editForm.requirement_type === 'range' && (
                  <div className="space-y-1">
                    <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Min</span>
                    <Input
                      type="number" step="any"
                      value={editForm.min_value}
                      onChange={(e) => setEdit('min_value', e.target.value)}
                      className="h-9 text-sm font-mono"
                      data-testid="qp-edit-min"
                    />
                  </div>
                )}
                {(editForm.requirement_type === 'range' || editForm.requirement_type === 'max') && (
                  <div className="space-y-1">
                    <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Max</span>
                    <Input
                      type="number" step="any"
                      value={editForm.max_value}
                      onChange={(e) => setEdit('max_value', e.target.value)}
                      className="h-9 text-sm font-mono"
                      data-testid="qp-edit-max"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Comment</span>
                <Input
                  value={editForm.comment}
                  onChange={(e) => setEdit('comment', e.target.value)}
                  placeholder="Optional note"
                  className="h-9 text-sm"
                  data-testid="qp-edit-comment"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditParam(null)} className="border-[#EDE6D8] rounded-lg" data-testid="qp-edit-cancel">
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              disabled={savingEdit}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
              data-testid="qp-edit-save"
            >
              {savingEdit ? <Spinner size={14} className="animate-spin mr-2" /> : <FloppyDisk size={14} className="mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CollapsibleSection>
  );
}
